import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import { useAppSelector } from "../../../redux/hooks";
import {
  selectSelectedLeague,
  selectSelectedLeagueId,
} from "../../../redux/slice/selectedLeagueSlice";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Api } from "../../../services/interceptor";
import { Images } from "../../../utils/imageConstants";
import BreadCrumb from "../../../components/BreadCrumb";
import CreateGroundModal from "../../myLeagues/components/CreateGroundModal";
import type { GroundFormData } from "../../myLeagues/components/CreateGroundModal";
import { bulkUploadGround } from "../../myLeagues/api/myLeaguesApi";
import { buildGroundTimingOpen } from "../../myLeagues/utils/groundPayload";
import { canAddGroundInLeague } from "../../../utils/leagueAccess";

interface Ground {
  groundId: string;
  groundName: string;
  groundAddress: string;
  email: string;
  imageUri: string | null;
  imageBucketLink: string | null;
  groundDescription: string;
  matchesPlayedTillDate: number;
  contactNumber: string;
  groundFacilities: string;
  daysOpen: string | null;
  timingOpen: string;
  canEdit: boolean;
  gmapLink: string | null;
}

const getGroundFormData = (ground?: Ground | null): GroundFormData | undefined => {
  if (!ground) return undefined;
  const [fromTime = "", toTime = ""] = String(ground.timingOpen ?? "")
    .split(/\s*-\s*/)
    .map((value) => value.trim());

  return {
    name: ground.groundName ?? "",
    location: ground.groundAddress ?? "",
    email: ground.email ?? "",
    contactNumber: ground.contactNumber ?? "",
    fromTime,
    toTime,
    selectDays: ground.daysOpen ?? "",
    description: ground.groundDescription ?? "",
    gmapLink: ground.gmapLink ?? "",
    groundFacilities: ground.groundFacilities ?? "",
  };
};

const getGroundErrorMessage = (error: unknown, fallback: string) => {
  const response = (error as { response?: { data?: unknown } })?.response;
  const data = response?.data;

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data && typeof data === "object") {
    const message = (data as { message?: unknown; error?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;

    const err = (data as { error?: unknown }).error;
    if (typeof err === "string" && err.trim()) return err;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const Grounds: React.FC = () => {
  const queryClient = useQueryClient();
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGround, setEditingGround] = useState<Ground | null>(null);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedGroundIds, setSelectedGroundIds] = useState<string[]>([]);
  const [deleteConfirmGroundIds, setDeleteConfirmGroundIds] = useState<string[]>([]);

  // Fetch grounds data using React Query
  const { data: grounds, isLoading } = useQuery<Ground[]>({
    queryKey: [QUERY_KEY.groundsByLeagueId, selectedLeagueId],
    enabled: !!selectedLeagueId,
    queryFn: async () => {
      if (!selectedLeagueId) return [];
      const response = await Api.get<Ground[]>(
        `${QUERY_KEY.groundsByLeagueId}/${selectedLeagueId}`
      );
      return Array.isArray(response?.data) ? response.data : [];
    },
  });

  const createGroundMutation = useMutation({
    mutationFn: async (form: GroundFormData) => {
      if (!selectedLeagueId) {
        throw new Error("League ID is missing.");
      }

      const payload = {
        groundName: form.name.trim(),
        groundAddress: form.location.trim(),
        email: form.email.trim(),
        gmapLink: form.gmapLink.trim(),
        imageBucketLink: "",
        groundDescription: form.description.trim(),
        contactNumber: form.contactNumber.trim(),
        groundFacilities: form.groundFacilities.trim(),
        daysOpen: form.selectDays.trim(),
        matchesPlayedTillDate: 0,
        timingOpen:
          buildGroundTimingOpen({
            fromTime: form.fromTime,
            toTime: form.toTime,
          }) ?? "",
      };

      return bulkUploadGround(selectedLeagueId, { grounds: [payload] });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.groundsByLeagueId, selectedLeagueId],
      });
      toast.success("Ground created successfully.");
      setIsModalOpen(false);
      setEditingGround(null);
    },
    onError: (error) => {
      toast.error(getGroundErrorMessage(error, "Failed to create ground."));
    },
  });

  const updateGroundMutation = useMutation({
    mutationFn: async ({ ground, form }: { ground: Ground; form: GroundFormData }) => {
      const formData = new FormData();
      formData.append("groundId", ground.groundId);
      formData.append("groundName", form.name.trim());
      formData.append("groundAddress", form.location.trim());
      formData.append("email", form.email.trim());
      formData.append("gmapLink", form.gmapLink.trim());
      formData.append("groundDescription", form.description.trim());
      formData.append("contactNumber", form.contactNumber.trim());
      formData.append("groundFacilities", form.groundFacilities.trim());
      formData.append("daysOpen", form.selectDays.trim());
      formData.append(
        "timingOpen",
        buildGroundTimingOpen({
          fromTime: form.fromTime,
          toTime: form.toTime,
        }) ?? ""
      );
      formData.append("matchesPlayedTillDate", String(Math.max(0, ground.matchesPlayedTillDate ?? 0)));

      const response = await Api.post<string>(QUERY_KEY.updateGround, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return typeof response?.data === "string" ? response.data : String(response?.data ?? "");
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.groundsByLeagueId, selectedLeagueId],
        }),
        editingGround?.groundId
          ? queryClient.invalidateQueries({
              queryKey: [QUERY_KEY.getGroundDetails, editingGround.groundId],
            })
          : Promise.resolve(),
      ]);
      toast.success("Ground updated successfully.");
      setIsModalOpen(false);
      setEditingGround(null);
    },
    onError: (error) => {
      toast.error(getGroundErrorMessage(error, "Failed to update ground."));
    },
  });

  const deleteGroundMutation = useMutation({
    mutationFn: async (groundIds: string[]) => {
      const responses = await Promise.all(
        groundIds.map(async (groundId) => {
          const response = await Api.delete<string>(`${QUERY_KEY.deleteGround}/${groundId}`);
          return typeof response?.data === "string"
            ? response.data
            : String(response?.data ?? "");
        })
      );

      return responses;
    },
    onSuccess: async (messages, groundIds) => {
      const failedMessage = messages.find(
        (message) => typeof message === "string" && message.toLowerCase().includes("failed")
      );
      if (failedMessage) {
        toast.error(failedMessage);
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.groundsByLeagueId, selectedLeagueId],
      });
      setIsDeleteMode(false);
      setSelectedGroundIds((prev) => prev.filter((id) => !groundIds.includes(id)));
      setDeleteConfirmGroundIds([]);
      toast.success(
        groundIds.length > 1
          ? "Selected grounds deleted successfully."
          : "Ground deleted successfully."
      );
    },
    onError: (error) => {
      toast.error(getGroundErrorMessage(error, "Failed to delete ground."));
    },
  });

  const isSubmitting =
    createGroundMutation.isPending ||
    updateGroundMutation.isPending ||
    deleteGroundMutation.isPending;

  const canManageGrounds =
    canAddGroundInLeague(selectedLeague) ||
    !!grounds?.some((ground) => ground.canEdit);

  useEffect(() => {
    if (!grounds?.length) {
      setSelectedGroundIds([]);
      return;
    }

    const deletableGroundIds = new Set(
      grounds
        .filter(
          (ground) => ground.canEdit && (ground.matchesPlayedTillDate ?? 0) === 0
        )
        .map((ground) => ground.groundId)
    );

    setSelectedGroundIds((prev) => prev.filter((id) => deletableGroundIds.has(id)));
  }, [grounds]);

  // Get image URL for a ground, or null when no real image has been uploaded
  const getGroundImage = (ground: Ground): string | null => {
    const cdnBaseUrl = import.meta.env.VITE_IMAGE_CDN_BASE_URL;
    if (ground?.imageUri) {
      if (cdnBaseUrl) {
        // Handle trailing/leading slashes
        const baseUrl = cdnBaseUrl.endsWith('/') ? cdnBaseUrl.slice(0, -1) : cdnBaseUrl;
        const imagePath = ground.imageUri.startsWith('/') ? ground.imageUri : `/${ground.imageUri}`;
        return `${baseUrl}${imagePath}`;
      }
      return ground.imageUri;
    }
    return null;
  };

  // Extract city from address (take first part before comma)
  const getCityFromAddress = (address: string): string => {
    if (!address) return "";
    const parts = address.split(",");
    return parts.length > 1 ? parts[parts.length - 2].trim().toUpperCase() : address.toUpperCase();
  };

  const openCreateModal = () => {
    if (!canManageGrounds) {
      toast.error("You do not have permission to add grounds for this league.");
      return;
    }
    setEditingGround(null);
    setIsModalOpen(true);
  };

  const openEditModal = (ground: Ground) => {
    setEditingGround(ground);
    setIsModalOpen(true);
  };

  const openDeleteMode = () => {
    setSelectedGroundIds([]);
    setIsDeleteMode(true);
  };

  const closeDeleteMode = () => {
    if (deleteGroundMutation.isPending) return;
    setSelectedGroundIds([]);
    setDeleteConfirmGroundIds([]);
    setIsDeleteMode(false);
  };

  const handleSubmitGround = async (form: GroundFormData) => {
    if (editingGround) {
      await updateGroundMutation.mutateAsync({ ground: editingGround, form });
      return;
    }
    await createGroundMutation.mutateAsync(form);
  };

  const toggleGroundSelection = (groundId: string) => {
    setSelectedGroundIds((prev) =>
      prev.includes(groundId)
        ? prev.filter((id) => id !== groundId)
        : [...prev, groundId]
    );
  };

  const handleBulkDeleteGrounds = async () => {
    if (selectedGroundIds.length === 0) return;
    setDeleteConfirmGroundIds(selectedGroundIds);
  };

  const closeDeleteConfirm = () => {
    if (deleteGroundMutation.isPending) return;
    setDeleteConfirmGroundIds([]);
  };

  const confirmDeleteGrounds = async () => {
    if (deleteConfirmGroundIds.length === 0) return;
    await deleteGroundMutation.mutateAsync(deleteConfirmGroundIds);
  };

  const deletableGroundCount =
    grounds?.filter(
      (ground) => ground.canEdit && (ground.matchesPlayedTillDate ?? 0) === 0
    ).length ?? 0;

  return (
    <div className="main-middle-content-wrap">
      <div className="grounds-wrapper custom-container">
        <BreadCrumb />
        <div className="grounds-header">
          <h1 className="heading">Grounds</h1>
          <div className="grounds-header-right">
            <div className="grounds-view-switch" role="group" aria-label="Grounds view mode">
              <button
                type="button"
                className={`grounds-view-toggle ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
                aria-pressed={viewMode === "grid"}
              >
                <img src={Images.card_show_icon} alt="" />
              </button>
              <button
                type="button"
                className={`grounds-view-toggle ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label="List view"
                aria-pressed={viewMode === "list"}
              >
                <img src={Images.list_view_icon} alt="" />
              </button>
            </div>
            {canManageGrounds && (
              <div className="grounds-header-actions">
                {isDeleteMode ? (
                  <>
                    <button
                      type="button"
                      className="grounds-btn grounds-btn--secondary"
                      onClick={closeDeleteMode}
                      disabled={deleteGroundMutation.isPending}
                    >
                      <span>Cancel</span>
                    </button>
                    <button
                      type="button"
                      className="grounds-btn grounds-btn--danger"
                      onClick={handleBulkDeleteGrounds}
                      disabled={deleteGroundMutation.isPending || selectedGroundIds.length === 0}
                    >
                      <FiTrash2 size={16} />
                      <span>
                        Delete Selected
                        {selectedGroundIds.length > 1 ? ` (${selectedGroundIds.length})` : ""}
                      </span>
                    </button>
                  </>
                ) : (
                  <>
                    {deletableGroundCount > 0 && (
                      <button
                        type="button"
                        className="grounds-btn grounds-btn--danger"
                        onClick={openDeleteMode}
                        disabled={deleteGroundMutation.isPending}
                      >
                        <FiTrash2 size={16} />
                        <span>Delete</span>
                      </button>
                    )}
                    <button
                      type="button"
                      className="grounds-btn grounds-btn--primary"
                      onClick={openCreateModal}
                      disabled={isSubmitting || !selectedLeagueId}
                    >
                      <FiPlus size={16} />
                      <span>Add Ground</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="grounds-loading">Loading...</div>
        ) : !grounds || grounds.length === 0 ? (
          <div className="no-data-found">
            <img src={Images.my_leagues_no} alt="No Grounds Found" />
            <p className="no-data-found-text">No Grounds Found</p>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grounds-grid">
            {grounds.map((ground) => (
              (() => {
                const hasPlayedMatches = (ground.matchesPlayedTillDate ?? 0) > 0;
                const groundImageUrl = getGroundImage(ground);
                return (
              <div
                className={`ground-card ${
                  isDeleteMode && selectedGroundIds.includes(ground.groundId)
                    ? "is-selected"
                    : ""
                }`}
                key={ground.groundId}
                onClick={() => {
                  if (isDeleteMode && !hasPlayedMatches && ground.canEdit) {
                    toggleGroundSelection(ground.groundId);
                    return;
                  }

                  navigate(`/ground-details/${ground.groundId}`);
                }}
                style={{ cursor: "pointer" }}
              >
                {ground.canEdit && (
                  <div className="ground-card-actions">
                    {isDeleteMode && !hasPlayedMatches && (
                      <button
                        type="button"
                        className={`ground-card-action ground-card-action--select ${
                          selectedGroundIds.includes(ground.groundId) ? "is-selected" : ""
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleGroundSelection(ground.groundId);
                        }}
                        aria-label={`Select ${ground.groundName}`}
                        title="Select ground"
                      />
                    )}
                    {!isDeleteMode && (
                      <button
                        type="button"
                        className="ground-card-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditModal(ground);
                        }}
                        aria-label={`Edit ${ground.groundName}`}
                      >
                        <FiEdit2 size={15} />
                      </button>
                    )}
                  </div>
                )}
                <div className="ground-img-wrapper">
                  {groundImageUrl ? (
                    <img
                      className="ground-img"
                      src={groundImageUrl}
                      alt={ground.groundName}
                    />
                  ) : (
                    <div className="ground-img-placeholder">
                      <img src={Images.camera_icon} alt="" className="ground-img-placeholder-icon" />
                      <span>No image available</span>
                    </div>
                  )}
                </div>
                <div className="overlay"></div>
                <div className="content">
                  <div className="ground-info">
                    <p className="ground-name">{ground.groundName}</p>
                    <p className="ground-city">
                      {getCityFromAddress(ground.groundAddress) || ground.groundAddress}
                    </p>
                  </div>
                  <div className="divider-line"></div>
                  <div className="ground-meta">
                    <p className="meta-item">
                      <span className="meta-label">Matches:</span>{" "}
                      <span className="meta-value">{ground.matchesPlayedTillDate}</span>
                    </p>
                    {ground.timingOpen && (
                      <p className="meta-item">
                        <span className="meta-label">Timing:</span>{" "}
                        <span className="meta-value">
                          {ground.timingOpen.replace("-", " - ")}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
                );
              })()
            ))}
          </div>
        ) : (
          <div className="grounds-list-wrap">
            <table className="grounds-list-table">
              <thead>
                <tr>
                  <th>GROUND</th>
                  <th>ADDRESS</th>
                  <th>CONTACT</th>
                  <th>MATCHES</th>
                  <th>TIMING</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {grounds.map((ground) => {
                  const hasPlayedMatches = (ground.matchesPlayedTillDate ?? 0) > 0;
                  const isSelected =
                    isDeleteMode && selectedGroundIds.includes(ground.groundId);

                  return (
                    <tr
                      key={ground.groundId}
                      className={isSelected ? "is-selected" : ""}
                      onClick={() => {
                        if (isDeleteMode && !hasPlayedMatches && ground.canEdit) {
                          toggleGroundSelection(ground.groundId);
                          return;
                        }
                        navigate(`/ground-details/${ground.groundId}`);
                      }}
                    >
                      <td>
                        <span className="grounds-list-name">{ground.groundName}</span>
                      </td>
                      <td>
                        <span className="grounds-list-address">{ground.groundAddress}</span>
                      </td>
                      <td>
                        <span className="grounds-list-value">
                          {ground.contactNumber || ground.email || "-"}
                        </span>
                      </td>
                      <td>
                        <span className="grounds-list-value">{ground.matchesPlayedTillDate}</span>
                      </td>
                      <td>
                        <span className="grounds-list-value">
                          {ground.timingOpen ? ground.timingOpen.replace("-", " - ") : "-"}
                        </span>
                      </td>
                      <td>
                        {ground.canEdit ? (
                          isDeleteMode && !hasPlayedMatches ? (
                            <button
                              type="button"
                              className={`ground-card-action ground-card-action--select ${
                                selectedGroundIds.includes(ground.groundId) ? "is-selected" : ""
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleGroundSelection(ground.groundId);
                              }}
                              aria-label={`Select ${ground.groundName}`}
                              title="Select ground"
                            />
                          ) : !isDeleteMode ? (
                            <button
                              type="button"
                              className="grounds-list-edit-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditModal(ground);
                              }}
                              aria-label={`Edit ${ground.groundName}`}
                            >
                              <FiEdit2 size={15} />
                            </button>
                          ) : (
                            <span className="grounds-list-value">-</span>
                          )
                        ) : (
                          <span className="grounds-list-value">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <CreateGroundModal
          open={isModalOpen}
          onOpenChange={(open) => {
            setIsModalOpen(open);
            if (!open) setEditingGround(null);
          }}
          onSubmit={handleSubmitGround}
          initialData={getGroundFormData(editingGround)}
          title={editingGround ? "Edit Ground" : "Add Ground"}
          submitLabel={editingGround ? "Save Changes" : "Add Ground"}
          description={
            editingGround
              ? "Update your ground details carefully. All editable text fields are sent on save."
              : "Manually add your cricket grounds to set up perfect matches. Ensure all details are accurate for scheduling!"
          }
        />
        {deleteConfirmGroundIds.length > 0 &&
          createPortal(
            <div
              className="ground-delete-modal-overlay"
              onClick={closeDeleteConfirm}
              aria-modal="true"
              role="dialog"
            >
              <div
                className="ground-delete-modal"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="ground-delete-modal__icon">
                  <FiTrash2 size={34} />
                </div>
                <h2 className="ground-delete-modal__title">
                  {deleteConfirmGroundIds.length > 1 ? "Delete Grounds ?" : "Delete Ground ?"}
                </h2>
                <p className="ground-delete-modal__text">
                  Your Ground{deleteConfirmGroundIds.length > 1 ? "s" : ""} will be permanently deleted.
                </p>
                <div className="ground-delete-modal__actions">
                  <button
                    type="button"
                    className="ground-delete-modal__btn ground-delete-modal__btn--cancel"
                    onClick={closeDeleteConfirm}
                    disabled={deleteGroundMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ground-delete-modal__btn ground-delete-modal__btn--delete"
                    onClick={() => void confirmDeleteGrounds()}
                    disabled={deleteGroundMutation.isPending}
                  >
                    {deleteGroundMutation.isPending ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default Grounds;
